import React, { useState } from 'react';
import { motion } from 'framer-motion';
import environmentConfig from '../config/environment';

const AccountSettings = ({ user, onBack, onUpdateProfile, onChangePassword, error, success, onLogout, theme, onToggleTheme }) => {
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    await onUpdateProfile({ first_name: firstName });
    setIsUpdatingProfile(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      alert('New password must be at least 6 characters long');
      return;
    }
    setIsChangingPassword(true);
    await onChangePassword(currentPassword, newPassword);
    if (!error) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
    setIsChangingPassword(false);
  };

  return (
    <div className="account-settings">
      <div className="account-settings-header">
        <button onClick={onBack} className="icon-button">←</button>
        <h2>Account Settings</h2>
        <span className="version-number">v{process.env.REACT_APP_VERSION}</span>
      </div>

      {error && (
        <motion.div initial={{opacity: 0, y: -10}} animate={{opacity: 1, y: 0}} className="error-message">
          {error}
        </motion.div>
      )}

      {success && (
        <motion.div initial={{opacity: 0, y: -10}} animate={{opacity: 1, y: 0}} className="success-message">
          {success}
        </motion.div>
      )}

      <div className="settings-section">
        <h3>Profile Information</h3>
        <form onSubmit={handleProfileUpdate} className="settings-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input type="email" id="email" value={user?.email || ''} disabled />
          </div>
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
            />
          </div>
          <div className="form-group">
            <label>AI Access Status</label>
            <div className={`ai-status-indicator ${user?.can_use_ai ? 'enabled' : 'disabled'}`}>
              <span className="ai-status-icon">
                {user?.can_use_ai ? '🤖' : '🚫'}
              </span>
              <span className="ai-status-text">
                {user?.can_use_ai ? 'AI Features Enabled' : 'AI Features Disabled'}
              </span>
            </div>
          </div>
          <button
            type="submit"
            className="primary-button"
            disabled={isUpdatingProfile}
          >
            {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
          </button>
        </form>
      </div>

      <div className="settings-section">
        <h3>Change Password</h3>
        <form onSubmit={handlePasswordChange} className="settings-form">
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength="6"
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength="6"
            />
          </div>
          <button
            type="submit"
            className="primary-button"
            disabled={isChangingPassword}
          >
            {isChangingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Preferences Section */}
      <div className="settings-section">
        <h3>Preferences</h3>
        <div className="settings-row" style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'center', padding: '12px 0' }}>
          <div>
            <p style={{ fontWeight: '600', margin: 0 }}>Dark Mode</p>
            <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0 0 0' }}>
              {theme === 'dark' ? 'Currently using dark theme' : 'Currently using light theme'}
            </p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={theme === 'dark'}
              onChange={onToggleTheme}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Environment Section */}
      <div className="settings-section">
        <h3>Environment</h3>
        <div className="settings-row" style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'center', padding: '12px 0' }}>
          <div>
            <p style={{ fontWeight: '600', margin: 0 }}>
              {environmentConfig.isProduction() ? 'Production' : 'Local Emulator'}
            </p>
            <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0 0 0' }}>
              API: {environmentConfig.getApiBaseUrl()}
            </p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={environmentConfig.isProduction()}
              onChange={() => environmentConfig.toggleEnvironment()}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '4px 0 0 0' }}>
          Switching will reload the app and require re-login.
        </p>
      </div>

      {/* Logout Section */}
      <div className="settings-section">
        <button
          onClick={onLogout}
          className="primary-button"
          style={{ background: '#EF4444', width: '100%' }}
        >
          Log Out
        </button>
      </div>
    </div>
  );
};

export default AccountSettings;
